import { create } from 'zustand'
import {
  Agent,
  ChatMessage,
  ChatRuntime,
  Session,
  SessionStatus,
  SkillCall,
  ToolCall,
  KnowledgeRetrievalCall,
} from '../types'
import { genId, nowIso } from '../utils/format'
import { useAgentStore } from './agentStore'
import { useSkillStore } from './skillStore'
import { useKnowledgeStore } from './knowledgeStore'

interface TemporaryInput {
  modelId: string
  skillIds: string[]
  knowledgeBaseIds: string[]
}

interface SessionStore {
  sessions: Session[]
  activeSessionId: string | null
  messages: Record<string, ChatMessage[]>
  startSession: (agent: Agent, modelId: string, isTest?: boolean) => string
  startTemporarySession: (input: TemporaryInput) => string
  deleteSession: (sessionId: string) => void
  setActiveSession: (id: string | null) => void
  sendMessage: (sessionId: string, runtime: ChatRuntime, text: string) => void
  interrupt: (sessionId: string) => void
}

const REPLY_SAMPLES: { text: string; useTool?: boolean; useSkill?: boolean }[] =
  [
    {
      text: '收到，我先检查目标服务器的运行状态。\n\n根据采集到的指标：\n- **CPU**：负载正常 (0.8)\n- **内存**：使用率 72%\n- **磁盘**：`/var/log` 分区使用率 **91%**，需要关注\n\n建议清理过期日志或扩容日志分区。需要我生成清理脚本吗？',
      useTool: true,
      useSkill: true,
    },
    {
      text: '好的，这是一个使用 React + TypeScript 的实现思路：\n\n```ts\nconst sum = (a: number, b: number) => a + b\n```\n\n关键点：\n1. 使用纯函数，便于测试\n2. 显式类型标注\n\n需要我补充单元测试吗？',
      useTool: true,
    },
    {
      text: '我已整理出以下要点摘要，并给出结构化结论。如需更深入的分析，可以指定具体维度。',
    },
  ]

function setStatus(sessionId: string, status: SessionStatus) {
  useSessionStore.setState((s) => ({
    sessions: s.sessions.map((se) =>
      se.sessionId === sessionId ? { ...se, status, updatedAt: nowIso() } : se,
    ),
  }))
}

function updateMessage(
  sessionId: string,
  msgId: string,
  patch: Partial<ChatMessage>,
) {
  useSessionStore.setState((s) => ({
    messages: {
      ...s.messages,
      [sessionId]: (s.messages[sessionId] || []).map((m) =>
        m.id === msgId ? { ...m, ...patch } : m,
      ),
    },
  }))
}

const DH_SESSION_ID = 'session_dhuman_demo'

const seedSessions: Session[] = [
  {
    sessionId: DH_SESSION_ID,
    sessionType: 'agent',
    agentId: 'agent_dhuman',
    agentName: '数字人口播 Agent',
    modelId: 'model_claude_sonnet',
    skillIds: ['skill_digital_human'],
    knowledgeBaseIds: [],
    status: 'idle',
    title: '数字人口播 Agent',
    lastMessagePreview: '已生成口播视频，时长 24 秒…',
    createdAt: '2026-06-05T10:10:00Z',
    updatedAt: '2026-06-05T10:15:00Z',
  },
]

const seedMessages: Record<string, ChatMessage[]> = {
  [DH_SESSION_ID]: [
    {
      id: 'msg_dh_1',
      role: 'user',
      content:
        '📎 [已上传图片：my_photo.jpg]\n\n帮我用这张照片作为数字人形象，配合这段文案生成口播视频：「大家好，我是本次直播的 AI 助手。今天将为大家演示如何快速接入 Agent 平台。」要求音色知性，加中文字幕。',
      createdAt: '2026-06-05T10:10:10Z',
    },
    {
      id: 'msg_dh_2',
      role: 'agent',
      content:
        '已接收图片并完成数字人形象克隆。口播视频已生成 ✅\n\n- **形象**：自定义图片克隆（my_photo.jpg）\n- **音色**：知性女声\n- **分辨率**：1080p\n- **时长**：24 秒\n- **字幕**：中文（已内嵌）\n\n📹 视频下载：[digital-human-custom.mp4](https://example.com/render/dh_custom_24s.mp4)\n\n视频已根据你的照片进行了面部驱动与口型同步。需要调整文案或更换音色吗？',
      streaming: false,
      knowledgeCalls: [],
      skillCalls: [
        {
          id: 'sk_dh_1',
          skillName: '数字人口播生成 Skill',
          version: '1.2.0',
          inputSummary: '自定义形象 (my_photo.jpg) · 脚本 42 字 · 知性音色',
          outputSummary: '生成 1080p 视频 24s，含口型同步',
          status: 'success',
          durationMs: 32000,
        },
      ],
      toolCalls: [
        {
          id: 'tc_dh_1',
          name: 'clone_avatar',
          input: '{"image":"my_photo.jpg"}',
          output: '{"avatarId":"custom_avatar_001"}',
          status: 'success',
          durationMs: 4500,
        },
        {
          id: 'tc_dh_2',
          name: 'render_video',
          input:
            '{"avatarId":"custom_avatar_001","script":"大家好...","voice":"intellectual_female","subtitle":"zh"}',
          output:
            '{"url":"https://example.com/render/dh_custom_24s.mp4","durationSec":24}',
          status: 'success',
          durationMs: 27500,
        },
      ],
      createdAt: '2026-06-05T10:15:00Z',
    },
  ],
}

export const useSessionStore = create<SessionStore>((set, get) => ({
  sessions: seedSessions,
  activeSessionId: null,
  messages: seedMessages,
  startSession: (agent, modelId, isTest) => {
    const sessionId = genId('session')
    const session: Session = {
      sessionId,
      sessionType: 'agent',
      agentId: agent.agentId,
      agentName: agent.name,
      modelId,
      skillIds: agent.skillIds || [],
      knowledgeBaseIds: agent.knowledgeBaseIds || [],
      status: 'idle',
      title: isTest ? `测试 · ${agent.name}` : agent.name,
      lastMessagePreview: '',
      isTest,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    set((s) => ({
      sessions: [session, ...s.sessions],
      activeSessionId: sessionId,
      messages: { ...s.messages, [sessionId]: [] },
    }))
    return sessionId
  },
  startTemporarySession: (input) => {
    const sessionId = genId('session')
    const session: Session = {
      sessionId,
      sessionType: 'temporary',
      modelId: input.modelId,
      skillIds: input.skillIds,
      knowledgeBaseIds: input.knowledgeBaseIds,
      status: 'idle',
      title: '临时会话',
      lastMessagePreview: '',
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    set((s) => ({
      sessions: [session, ...s.sessions],
      activeSessionId: sessionId,
      messages: { ...s.messages, [sessionId]: [] },
    }))
    return sessionId
  },
  deleteSession: (sessionId) =>
    set((s) => {
      const { [sessionId]: _removed, ...restMessages } = s.messages
      return {
        sessions: s.sessions.filter((se) => se.sessionId !== sessionId),
        messages: restMessages,
        activeSessionId:
          s.activeSessionId === sessionId ? null : s.activeSessionId,
      }
    }),
  setActiveSession: (id) => set({ activeSessionId: id }),
  interrupt: (sessionId) => {
    setStatus(sessionId, 'interrupted')
    const msgs = get().messages[sessionId] || []
    const streamingMsg = msgs.find((m) => m.streaming)
    if (streamingMsg) {
      updateMessage(sessionId, streamingMsg.id, {
        streaming: false,
        content: streamingMsg.content + '\n\n_（已中断）_',
      })
    }
  },
  sendMessage: (sessionId, runtime, text) => {
    const userMsg: ChatMessage = {
      id: genId('msg'),
      role: 'user',
      content: text,
      createdAt: nowIso(),
    }
    const agentMsgId = genId('msg')

    // Detect if this is a digital human request
    const isDigitalHuman =
      runtime.agentId === 'agent_dhuman' ||
      text.includes('数字人') ||
      text.includes('口播')

    const dhSample = {
      text: '已接收图片并完成数字人形象克隆。口播视频已生成 ✅\n\n- **形象**：自定义图片克隆\n- **音色**：知性女声\n- **分辨率**：1080p\n- **时长**：24 秒\n- **字幕**：中文（已内嵌）\n\n📹 视频下载：[digital-human-custom.mp4](https://example.com/render/dh_custom_24s.mp4)\n\n视频已根据你的照片进行了面部驱动与口型同步。需要调整文案或更换音色吗？',
      useTool: true,
      useSkill: true,
    }

    const sample = isDigitalHuman
      ? dhSample
      : REPLY_SAMPLES[Math.floor(Math.random() * REPLY_SAMPLES.length)]

    set((s) => ({
      messages: {
        ...s.messages,
        [sessionId]: [
          ...(s.messages[sessionId] || []),
          userMsg,
          {
            id: agentMsgId,
            role: 'agent',
            content: '',
            streaming: true,
            toolCalls: [],
            skillCalls: [],
            knowledgeCalls: [],
            createdAt: nowIso(),
          },
        ],
      },
      sessions: s.sessions.map((se) =>
        se.sessionId === sessionId
          ? {
              ...se,
              lastMessagePreview: text.slice(0, 40),
              updatedAt: nowIso(),
            }
          : se,
      ),
    }))
    setStatus(sessionId, 'running')

    const hasKb = runtime.knowledgeBases && runtime.knowledgeBases.length > 0
    const hasSkill = runtime.skills && runtime.skills.length > 0

    setTimeout(() => {
      if (
        get().sessions.find((se) => se.sessionId === sessionId)?.status !==
        'running'
      )
        return

      // knowledge retrieval call
      if (hasKb) {
        const kb = runtime.knowledgeBases[0]
        const knowledgeCall: KnowledgeRetrievalCall = {
          id: genId('kc'),
          knowledgeBaseName: kb.name,
          query: text.slice(0, 40),
          status: 'running',
        }
        updateMessage(sessionId, agentMsgId, {
          knowledgeCalls: [knowledgeCall],
        })
        setTimeout(() => {
          updateMessage(sessionId, agentMsgId, {
            knowledgeCalls: [
              {
                ...knowledgeCall,
                status: 'success',
                durationMs: 480,
                hits: [
                  {
                    documentName: '产品功能说明.pdf',
                    snippet: `与「${text.slice(0, 20)}」相关的片段：相关业务说明与配置步骤摘要。`,
                    score: 0.91,
                  },
                  {
                    documentName: '常见问题 FAQ.md',
                    snippet: '该问题的常见处理方式与注意事项。',
                    score: 0.83,
                  },
                ],
              },
            ],
          })
        }, 700)
      }

      // skill call
      if (sample.useSkill && hasSkill) {
        const skill = runtime.skills[0]
        const skillCall: SkillCall = {
          id: genId('sk'),
          skillName: skill.name,
          version: skill.version,
          inputSummary: isDigitalHuman
            ? '自定义形象克隆 · 脚本 42 字'
            : '服务器状态检查',
          status: 'running',
        }
        updateMessage(sessionId, agentMsgId, { skillCalls: [skillCall] })
        setTimeout(() => {
          updateMessage(sessionId, agentMsgId, {
            skillCalls: [
              {
                ...skillCall,
                status: 'success',
                outputSummary: isDigitalHuman
                  ? '生成 1080p 视频 24s'
                  : '已采集 12 项指标',
                durationMs: isDigitalHuman ? 3200 : 1340,
              },
            ],
          })
        }, 900)
      }

      // tool call
      if (sample.useTool) {
        const toolCall: ToolCall = {
          id: genId('tc'),
          name: isDigitalHuman ? 'clone_avatar' : 'run_shell',
          input: isDigitalHuman
            ? '{"image":"uploaded_file"}'
            : 'df -h && free -m',
          status: 'running',
        }
        setTimeout(() => {
          updateMessage(sessionId, agentMsgId, { toolCalls: [toolCall] })
          setTimeout(() => {
            const toolCall2: ToolCall = {
              id: genId('tc'),
              name: 'render_video',
              input: '{"avatarId":"custom_001","script":"..."}',
              status: 'running',
            }
            if (isDigitalHuman) {
              updateMessage(sessionId, agentMsgId, {
                toolCalls: [
                  {
                    ...toolCall,
                    status: 'success',
                    output: '{"avatarId":"custom_001"}',
                    durationMs: 800,
                  },
                  toolCall2,
                ],
              })
              setTimeout(() => {
                updateMessage(sessionId, agentMsgId, {
                  toolCalls: [
                    {
                      ...toolCall,
                      status: 'success',
                      output: '{"avatarId":"custom_001"}',
                      durationMs: 800,
                    },
                    {
                      ...toolCall2,
                      status: 'success',
                      output: '{"url":"https://example.com/video.mp4"}',
                      durationMs: 2400,
                    },
                  ],
                })
              }, 1500)
            } else {
              updateMessage(sessionId, agentMsgId, {
                toolCalls: [
                  {
                    ...toolCall,
                    status: 'success',
                    output: '/dev/sda1 91% /var/log\nMem: 72% used',
                    durationMs: 620,
                  },
                ],
              })
            }
          }, 800)
        }, 600)
      }

      // stream text after tools / retrieval
      const startDelay = isDigitalHuman
        ? 4500
        : sample.useTool || (sample.useSkill && hasSkill) || hasKb
          ? 2200
          : 400
      setTimeout(() => {
        const full = sample.text
        let i = 0
        const tick = () => {
          const cur = get().sessions.find((se) => se.sessionId === sessionId)
          if (!cur || cur.status !== 'running') return
          i += Math.max(2, Math.floor(Math.random() * 5))
          updateMessage(sessionId, agentMsgId, { content: full.slice(0, i) })
          if (i < full.length) {
            setTimeout(tick, 28)
          } else {
            updateMessage(sessionId, agentMsgId, {
              content: full,
              streaming: false,
            })
            setStatus(sessionId, 'idle')
            useSessionStore.setState((s) => ({
              sessions: s.sessions.map((se) =>
                se.sessionId === sessionId
                  ? { ...se, lastMessagePreview: full.slice(0, 40) }
                  : se,
              ),
            }))
            if (runtime.agentId) {
              useAgentStore.getState().touchAgent(runtime.agentId)
            }
          }
        }
        tick()
      }, startDelay)
    }, 500)
  },
}))

// Build a ChatRuntime from an Agent
export function runtimeFromAgent(agent: Agent): ChatRuntime {
  return {
    name: agent.name,
    description: agent.description,
    agentId: agent.agentId,
    defaultModelId: agent.defaultModelId,
    skills: agent.skills || [],
    knowledgeBases: agent.knowledgeBases || [],
  }
}

// Build a ChatRuntime from a temporary session, resolving refs from stores
export function runtimeFromSession(session: Session): ChatRuntime {
  if (session.sessionType === 'agent' && session.agentId) {
    const agent = useAgentStore
      .getState()
      .agents.find((a) => a.agentId === session.agentId)
    if (agent) return runtimeFromAgent(agent)
  }
  const skills = useSkillStore
    .getState()
    .skills.filter((sk) => session.skillIds.includes(sk.skillId))
    .map((sk) => ({
      skillId: sk.skillId,
      name: sk.name,
      description: sk.description,
      version: sk.version,
    }))
  const knowledgeBases = useKnowledgeStore
    .getState()
    .knowledgeBases.filter((kb) =>
      session.knowledgeBaseIds.includes(kb.knowledgeBaseId),
    )
    .map((kb) => ({ knowledgeBaseId: kb.knowledgeBaseId, name: kb.name }))
  return {
    name: session.title,
    agentId: session.agentId,
    defaultModelId: session.modelId,
    skills,
    knowledgeBases,
  }
}
